<template>
  <div class="min-h-screen p-2 bg-gray-50">
    <div class="p-2 mb-2 bg-white rounded-lg shadow-sm">
      <div class="flex items-center justify-between">
        <h2 class="text-xl font-semibold text-gray-800">Gjendje</h2>

        <div class="flex items-center gap-4">
          <div class="flex flex-col">
            <label class="mb-1 text-xs text-gray-500">Pika e shitjes</label>
            <select v-model="selected_pos" @change="handlePosChange" class="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" title="Thekso magazinat e pikës së zgjedhur, pa i fshehur të tjerat">
              <option v-for="pos in pos_list" :key="pos.guid" :value="pos.guid">
                {{ pos.name }}
              </option>
            </select>
          </div>

          <div class="flex flex-col">
            <label class="mb-1 text-xs text-gray-500">Kërkimi</label>
            <div class="relative flex items-center">
              <input
                v-model="search_val"
                @input="debouncedSearch"
                class="w-full py-2 pr-3 text-sm bg-white border border-gray-300 rounded-md pl-9 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="text"
                placeholder="Kërko produkt..."
              />
              <div class="absolute flex items-center h-full text-gray-400 left-3">
                <i class="fa fa-search"></i>
              </div>
            </div>
          </div>

          <div class="flex items-center">
            <div>
              <label class="text-sm text-gray-500">Trego vetëm stokun kryesorë</label>
              <input type="checkbox" v-model="showMainStockOnly" class="text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            </div>
          </div>

          <button @click="refreshData" class="p-2 transition-colors rounded-full hover:bg-gray-100">
            <i class="text-gray-500 fa fa-refresh"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div v-if="isDataLoaded" class="overflow-x-auto bg-white rounded-lg shadow-sm">
      <table class="w-full border-collapse">
        <thead>
          <tr class="bg-gray-50">
            <th class="px-2 py-2 font-medium text-left text-gray-700 border-b">Produkti</th>
            <template v-for="warehouse in filteredWarehousesList" :key="warehouse.guid">
              <th class="px-4 py-3 text-xs text-center border-b" :class="posColumnClass(warehouse, 'group')" colspan="4">
                {{ warehouse.name.replace("MAGAZINA", "M.") }}
              </th>
            </template>
          </tr>
          <tr class="bg-gray-50">
            <th class="px-4 py-3 font-medium text-left text-gray-700 border-b"></th>
            <template v-for="warehouse in filteredWarehousesList" :key="warehouse.guid">
              <th class="px-3 py-2 text-sm font-medium text-center text-gray-600 border-b" :class="posColumnClass(warehouse, 'start')" title="Gjendja totale ne stok">Stok</th>
              <th class="px-3 py-2 text-sm font-medium text-center text-gray-600 border-b" :class="posColumnClass(warehouse, 'middle')" title="Gjendja e rezervuar">Ditar</th>
              <th class="px-3 py-2 text-sm font-medium text-center text-gray-600 border-b" :class="posColumnClass(warehouse, 'middle')" title="Gjendja ne proces">Proces</th>
              <th class="px-3 py-2 text-sm font-medium text-center text-gray-600 border-b" :class="posColumnClass(warehouse, 'end')" title="Gjendja e disponueshme">Disp.</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in displayedProducts" :key="product.guid" class="transition-colors hover:bg-blue-50">
            <td class="sticky left-0 px-4 py-3 bg-white border-b">
              {{ product.name }}
            </td>
            <template v-for="warehouse in filteredWarehousesList" :key="warehouse.guid">
              <!-- Stock -->
              <td class="px-4 py-3 text-xs text-center border-b" :class="posColumnClass(warehouse, 'start')">
                <div :class="[(product[warehouse.guid] || 0) == 0 ? 'text-gray-400' : 'border rounded-full p-1']" class="text-center">
                  {{ product[warehouse.guid] || 0 }}
                </div>
              </td>

              <!-- Diary -->
              <td class="px-4 py-3 text-center border-b" :class="posColumnClass(warehouse, 'middle')">
                <div class="flex justify-center gap-1">
                  <span v-if="product[warehouse.guid + '_diary_out']" class="text-red-600"> -{{ product[warehouse.guid + "_diary_out"] }} </span>
                  <span v-if="product[warehouse.guid + '_diary_in']" class="text-blue-600"> +{{ product[warehouse.guid + "_diary_in"] }} </span>
                </div>
              </td>

              <!-- Process -->
              <td class="px-4 py-3 text-center border-b" :class="posColumnClass(warehouse, 'middle')">
                <div class="flex justify-center gap-1">
                  <span v-if="product[warehouse.guid + '_out']" @click="toggle_stock_info_modal(product, warehouse)" class="text-red-600 cursor-pointer hover:underline"> -{{ product[warehouse.guid + "_out"] }} </span>
                  <span v-if="product[warehouse.guid + '_in']" @click="toggle_stock_info_modal(product, warehouse)" class="text-blue-600 cursor-pointer hover:underline"> +{{ product[warehouse.guid + "_in"] }} </span>
                </div>
              </td>

              <!-- Available -->
              <td class="px-4 py-3 text-center border-b" :class="posColumnClass(warehouse, 'end')">
                <span
                  :class="{
                    'text-gray-400': getAvailableStock(product, warehouse) == 0,
                    'font-semibold': getAvailableStock(product, warehouse) > 0,
                  }"
                >
                  {{ getAvailableStock(product, warehouse) }}
                </span>
              </td>
            </template>
          </tr>
        </tbody>
      </table>

      <!-- Pagination and Results Counter -->
      <div class="flex items-center justify-between p-4 border-t">
        <span class="text-sm text-gray-500"> {{ totalFilteredProducts }} nga {{ products_list.length }} produkte </span>
        <div class="flex items-center gap-2">
          <button @click="prevPage" :disabled="currentPage === 1" class="px-3 py-1 border rounded" :class="currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'hover:bg-gray-50'">
            <i class="fa fa-chevron-left"></i>
          </button>
          <span class="text-sm text-gray-600"> Faqe {{ currentPage }} nga {{ totalPages }} </span>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="px-3 py-1 border rounded" :class="currentPage === totalPages ? 'bg-gray-100 text-gray-400' : 'hover:bg-gray-50'">
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-500">Trego të gjitha</label>
          <input type="checkbox" v-model="showAllProducts" class="text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else class="flex items-center justify-center h-64">
      <div class="flex flex-col items-center gap-3">
        <i class="text-blue-500 fa fa-spinner fa-spin fa-2x"></i>
        <span class="text-gray-500">Faqja po ngarkohet...</span>
      </div>
    </div>

    <!-- Modals -->
    <add-stock-modal v-if="show_add_stock_modal" @toggle_stock_modal="toggle_stock_modal" />
    <stock-info-modal v-if="show_stock_info_modal" :selected_stock_product="selected_stock_product" :selected_stock_warehouse="selected_stock_warehouse" @toggle_stock_info_modal="toggle_stock_info_modal" />
  </div>
</template>

<script>
import Api from "@/services/Api";
import WarehousesServices from "@/services/Warehouse";
import ProductsServices from "@/services/Product";
import addStockModal from "./components/add-stock-modal";
import StockInfoModal from "./components/stock-info-modal";
import permissionMixin from "@/mixins/permissions-mixin.js";

export default {
  mixins: [permissionMixin],
  components: {
    addStockModal,
    StockInfoModal,
  },
  data() {
    return {
      warehouses: [],
      products: [],
      stock: [],
      stockMap: {}, // Cache for stock data by product and warehouse
      pos_list: [],
      selected_pos: null,
      all_pos_warehouses: [],
      search_val: "",
      pending_stock: [],
      diary_stock: [],
      show_add_stock_modal: false,
      show_stock_info_modal: false,
      selected_stock_product: {},
      selected_stock_warehouse: {},
      current_user: {},
      currentPage: 1,
      itemsPerPage: 15,
      showAllProducts: false,
      showMainStockOnly: false,
      isDataLoaded: false,
      filteredProducts: [], // Store the filtered products
      totalFilteredCount: 0,
      searchTimeout: null,
      filteredWarehousesList: [], // Cache filtered warehouses
    };
  },
  async created() {
    if (!this.am_allowed("stock.can_see")) {
      window.location.href = "/home";
    } else {
      try {
        this.warehouses = await WarehousesServices.getWarehouses();
        this.current_user = JSON.parse(localStorage.user);
        await this.get_user_pos();

        if (this.pos_list.length > 0) {
          await this.loadAllPosWarehouses();
          await this.get_stock();
        }
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    }
  },
  methods: {
    refreshData() {
      this.isDataLoaded = false;
      this.loadAllPosWarehouses().then(() => this.get_stock());
    },

    handlePosChange() {
      this.updateFilteredWarehouses();
    },

    debouncedSearch() {
      // Cancel previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set new timeout
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.applyFilters();
      }, 300);
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    async get_stock() {
      try {
        this.isDataLoaded = false;
        this.stock = [];
        this.pending_stock = [];
        this.diary_stock = [];
        this.stockMap = {};

        // Get products first
        this.products = await ProductsServices.getProducts();

        this.updateFilteredWarehouses();

        // Fetch stock data
        const response = await Api(true).post("stock/get/all");
        this.stock = response.data[0];
        this.pending_stock = response.data[1];
        this.diary_stock = response.data[2];

        // Pre-calculate stock data and cache it
        this.populateStockData();

        // Apply filters and pagination
        this.applyFilters();

        this.isDataLoaded = true;
      } catch (error) {
        console.error("Error fetching stock data:", error);
      }
    },

    populateStockData() {
      // First, create an efficient lookup map for stock data
      const stockLookup = {};
      const pendingLookup = {};
      const diaryLookup = {};

      // Build lookup tables
      this.stock.forEach((item) => {
        const key = `${item.product_id}_${item.warehouse_id}`;
        stockLookup[key] = (stockLookup[key] || 0) + Number(item.amount);
      });

      this.pending_stock.forEach((item) => {
        if (item.amount) {
          if (item.warehouse_in) {
            const keyIn = `${item.product_id}_${item.warehouse_in}_in`;
            pendingLookup[keyIn] = (pendingLookup[keyIn] || 0) + Number(item.amount);
          }
          if (item.warehouse_out) {
            const keyOut = `${item.product_id}_${item.warehouse_out}_out`;
            pendingLookup[keyOut] = (pendingLookup[keyOut] || 0) + Number(item.amount);
          }
        }
      });

      this.diary_stock.forEach((item) => {
        if (item.amount) {
          if (item.warehouse_diary_in) {
            const keyIn = `${item.entity_id}_${item.warehouse_diary_in}_diary_in`;
            diaryLookup[keyIn] = (diaryLookup[keyIn] || 0) + Number(item.amount);
          }
          if (item.warehouse_diary_out) {
            const keyOut = `${item.entity_id}_${item.warehouse_diary_out}_diary_out`;
            diaryLookup[keyOut] = (diaryLookup[keyOut] || 0) + Number(item.amount);
          }
        }
      });

      // Apply lookup data to products
      this.products = this.products.map((product) => {
        // Determine if product has any stock data
        let hasStock = false;

        // Clone the product to avoid modifying the original
        const enhancedProduct = { ...product };

        // Add stock data from lookups
        this.warehouses.forEach((warehouse) => {
          const stockKey = `${product.guid}_${warehouse.guid}`;
          const stockAmount = stockLookup[stockKey] || 0;

          const pendingInKey = `${product.guid}_${warehouse.guid}_in`;
          const pendingOutKey = `${product.guid}_${warehouse.guid}_out`;
          const pendingIn = pendingLookup[pendingInKey] || 0;
          const pendingOut = pendingLookup[pendingOutKey] || 0;

          const diaryInKey = `${product.guid}_${warehouse.guid}_diary_in`;
          const diaryOutKey = `${product.guid}_${warehouse.guid}_diary_out`;
          const diaryIn = diaryLookup[diaryInKey] || 0;
          const diaryOut = diaryLookup[diaryOutKey] || 0;

          // Set values on the product
          enhancedProduct[warehouse.guid] = stockAmount;
          enhancedProduct[warehouse.guid + "_in"] = pendingIn;
          enhancedProduct[warehouse.guid + "_out"] = pendingOut;
          enhancedProduct[warehouse.guid + "_diary_in"] = diaryIn;
          enhancedProduct[warehouse.guid + "_diary_out"] = diaryOut;

          // Check if product has any stock
          if (stockAmount > 0 || pendingIn > 0 || pendingOut > 0 || diaryIn > 0 || diaryOut > 0) {
            hasStock = true;
          }
        });

        enhancedProduct.has_stock = hasStock;
        return enhancedProduct;
      });
    },

    applyFilters() {
      // Start with all products
      let filtered = [...this.products];

      // Apply stock filter if showAllProducts is false
      if (!this.showAllProducts) {
        filtered = filtered.filter((product) => {
          let hasStock = false;
          // Only check warehouses that are visible
          this.filteredWarehousesList.forEach((w) => {
            if (product[w.guid] > 0 || product[w.guid + "_out"] > 0 || product[w.guid + "_in"] > 0 || product[w.guid + "_diary_out"] > 0 || product[w.guid + "_diary_in"] > 0) {
              hasStock = true;
            }
          });
          return hasStock;
        });
      }

      // Apply search filter
      if (this.search_val) {
        const searchTerm = this.search_val.toLowerCase();
        filtered = filtered.filter((product) => {
          return product.name.toLowerCase().includes(searchTerm) || (product.barcode && product.barcode.toLowerCase().includes(searchTerm)) || (product.sku && product.sku.toLowerCase().includes(searchTerm));
        });
      }

      // Save total count
      this.totalFilteredCount = filtered.length;

      // Save filtered products (before pagination)
      this.filteredProducts = filtered;
    },

    async get_user_pos() {
      try {
        const response = await Api(true).post("/invoice/get/assigned/pos", { user: this.current_user });
        this.pos_list = response.data;

        // Select first POS if available
        if (this.pos_list.length > 0) {
          this.selected_pos = this.pos_list[0].guid;
        }
      } catch (error) {
        console.error("Error fetching POS data:", error);
      }
    },

    async loadAllPosWarehouses() {
      const results = await Promise.all(
        this.pos_list.map(async (pos) => {
          try {
            const response = await Api(true).post("pos/warehouses/list", { pos_id: pos.guid });
            return Array.isArray(response.data) ? response.data : [];
          } catch (error) {
            console.error("Error fetching warehouses for POS:", pos.name, error);
            return [];
          }
        })
      );
      this.all_pos_warehouses = results.flat();
    },

    getWarehousePosSortKey(warehouse) {
      if (this.selectedPosWarehouseIds.has(warehouse.guid)) {
        return "";
      }
      const link = this.all_pos_warehouses.find((wp) => wp.warehouse_id === warehouse.guid);
      const pos = this.pos_list.find((p) => p.guid === link?.pos_id);
      return pos?.name || warehouse.name || "";
    },

    getWarehouseTypeId(warehouse) {
      const selectedLink = this.all_pos_warehouses.find((wp) => wp.warehouse_id === warehouse.guid && wp.pos_id === this.selected_pos);
      if (selectedLink) {
        return selectedLink.type_id || 0;
      }
      const anyLink = this.all_pos_warehouses.find((wp) => wp.warehouse_id === warehouse.guid);
      return anyLink?.type_id || 0;
    },

    isSelectedPosWarehouse(warehouse) {
      return this.selectedPosWarehouseIds.has(warehouse.guid);
    },

    isFirstSelectedWarehouse(warehouse) {
      const first = this.filteredWarehousesList.find((w) => this.isSelectedPosWarehouse(w));
      return first && first.guid === warehouse.guid;
    },

    isLastSelectedWarehouse(warehouse) {
      const selected = this.filteredWarehousesList.filter((w) => this.isSelectedPosWarehouse(w));
      return selected.length > 0 && selected[selected.length - 1].guid === warehouse.guid;
    },

    posColumnClass(warehouse, edge) {
      if (!this.isSelectedPosWarehouse(warehouse)) {
        return edge === "group" ? "text-gray-500" : "";
      }

      const classes = edge === "group" ? ["bg-sky-100", "text-sky-900", "font-semibold", "pos-col-selected-header"] : ["bg-sky-50", "pos-col-selected"];

      if ((edge === "group" || edge === "start") && this.isFirstSelectedWarehouse(warehouse)) {
        classes.push("pos-col-selected-start");
      }
      if ((edge === "group" || edge === "end") && this.isLastSelectedWarehouse(warehouse)) {
        classes.push("pos-col-selected-end");
      }

      return classes;
    },

    updateFilteredWarehouses() {
      const posWarehouseIds = new Set(this.all_pos_warehouses.map((wp) => wp.warehouse_id));
      const selectedIds = this.selectedPosWarehouseIds;

      let warehousesList = this.warehouses.filter((w) => posWarehouseIds.has(w.guid));

      warehousesList.sort((a, b) => {
        const aSelected = selectedIds.has(a.guid) ? 0 : 1;
        const bSelected = selectedIds.has(b.guid) ? 0 : 1;
        if (aSelected !== bSelected) {
          return aSelected - bSelected;
        }

        const posDiff = this.getWarehousePosSortKey(a).localeCompare(this.getWarehousePosSortKey(b));
        if (posDiff !== 0) {
          return posDiff;
        }

        const typeDiff = this.getWarehouseTypeId(a) - this.getWarehouseTypeId(b);
        if (typeDiff !== 0) {
          return typeDiff;
        }

        return (a.name || "").localeCompare(b.name || "");
      });

      if (this.showMainStockOnly) {
        warehousesList = warehousesList.filter((w) => {
          return w.warehouse_facility === 1 || w.warehouse_facility === 9;
        });
      }

      this.filteredWarehousesList = warehousesList;
    },

    toggle_stock_modal() {
      this.show_add_stock_modal = !this.show_add_stock_modal;
    },

    toggle_stock_info_modal(p, w) {
      this.show_stock_info_modal = !this.show_stock_info_modal;
      if (this.show_stock_info_modal) {
        this.selected_stock_product = p;
        this.selected_stock_warehouse = w;
      } else {
        this.selected_stock_product = {};
        this.selected_stock_warehouse = {};
      }
    },

    getAvailableStock(product, warehouse) {
      const stock = product[warehouse.guid] || 0;
      const diaryOut = product[warehouse.guid + "_diary_out"] || 0;
      const pendingOut = product[warehouse.guid + "_out"] || 0;

      const available = stock - diaryOut - pendingOut;
      return available > 0 ? available : "-";
    },
  },
  computed: {
    products_list() {
      return this.products;
    },

    displayedProducts() {
      // Get current page items
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.filteredProducts.slice(startIndex, endIndex);
    },

    totalFilteredProducts() {
      return this.totalFilteredCount;
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.totalFilteredCount / this.itemsPerPage));
    },

    selectedPosWarehouseIds() {
      return new Set(this.all_pos_warehouses.filter((wp) => wp.pos_id === this.selected_pos).map((wp) => wp.warehouse_id));
    },
  },
  watch: {
    showMainStockOnly() {
      this.currentPage = 1;
      this.updateFilteredWarehouses();
      this.applyFilters();
    },

    showAllProducts() {
      this.currentPage = 1;
      this.applyFilters();
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.pos-col-selected-header {
  box-shadow: inset 0 3px 0 0 #0ea5e9;
}

.pos-col-selected-start {
  border-left: 2px solid #38bdf8;
}

.pos-col-selected-end {
  border-right: 2px solid #38bdf8;
}
</style>
